import { useSelector } from 'react-redux';
import { GlobalState } from '../../../../../../store/types';
import './style.scss';

import minus from '../../../../../../assets/minus.svg';
import { getTranslations } from '../../../../../../utils/i18n';

type Props = {
  title: string;
  subtitle: string;
  toggleChat: () => void;
  showCloseButton: boolean;
  titleAvatar?: string;
  fullScreenMode: boolean;
  /**
   * IMBEE-8955 (T115) — the channel's open/closed state, shown as a dot and a word.
   *
   * Undefined when operating hours are off, and then nothing renders at all: AC-1.3 makes the
   * feature a strict no-op, so a channel without it must not suddenly advertise a status.
   */
  operatingHoursClosed?: boolean;
  /** Suppress the channel logo; the bar collapses with it only in full screen — see below (IMBEE-9667). */
  hideHeader?: boolean;
};

function Header({
  title,
  subtitle,
  toggleChat,
  showCloseButton,
  titleAvatar,
  fullScreenMode,
  operatingHoursClosed,
  hideHeader,
}: Props) {
  const parentUrl = useSelector((state: GlobalState) => state.responses.parentUrl);
  /**
   * `hideHeader` suppresses the channel logo everywhere, and drops the whole bar only where
   * dropping it costs nothing.
   *
   * In full screen the bar carries the logo and nothing else — the close button is already
   * suppressed because the host app supplies its own navigation — so collapsing it is the whole
   * point of the parameter.
   *
   * On a website embed the bar also carries the close button, and on a mobile device that button
   * is the ONLY way out: `layout.tsx` deliberately does not render the launcher while the chat is
   * open on mobile, because an open chat is full-screen there and a floating bubble over it makes
   * no sense. Removing the bar in that context leaves the visitor with no way to close the widget
   * at all. `isMobile` comes from react-device-detect and is USER-AGENT based, so resizing a
   * desktop browser cannot reproduce it — the launcher renders and the case looks fine.
   *
   * So: logo hidden in both, bar dropped only in full screen.
   *
   * IMBEE-8955 (T115) — with one exception, and it is the reason `hasStatusBadge` exists.
   *
   * "In full screen the bar carries the logo and nothing else" stopped being true when the
   * operating-hours badge landed in it. Left as written, the badge inherited a visibility rule
   * about the close button and the logo: it disappeared on `fullScreenMode=1` with no configured
   * logo, and again on `fullScreenMode=1` with `hideHeader` — an app embed that hides the logo.
   * In both the away notice the feature exists to show was simply absent, with no error.
   *
   * So the bar now survives wherever there is a status to show. That does give back the ~60px in
   * the one configuration that both hides the logo AND runs operating hours; a channel with the
   * feature off is untouched, because `operatingHoursClosed` is undefined and the old rule stands
   * exactly as it did. Showing the wrong state is worse than showing a bar.
   */
  const headerAvatar = hideHeader ? '' : titleAvatar;
  const hasStatusBadge = operatingHoursClosed !== undefined;
  const dropBar = Boolean(hideHeader) && fullScreenMode && !hasStatusBadge;
  const renderHeader =
    !dropBar && ((showCloseButton && !fullScreenMode) || !!headerAvatar || !!subtitle || hasStatusBadge);
  return renderHeader ? (
    <div className="rcw-header">
      <span className="rcw-subtitle">{subtitle}</span>
      <h4 className="rcw-title">
        {headerAvatar && <img src={headerAvatar} className="avatar" alt="profile" />}
        {/* {title} */}
      </h4>
      {operatingHoursClosed === undefined ? null : (
        <span
          className={`rcw-operating-status ${operatingHoursClosed ? 'is-closed' : 'is-open'}`}
          data-testid="operating-status"
          /*
           * `role="status"` because this badge CHANGES while the visitor is looking at it — the
           * boundary timer flips it at the schedule edge, with no navigation and no click. A
           * sighted user sees the word swap; without a live region a screen-reader user is told
           * nothing, and the in-thread StatusNotice (which does carry role="status") is not
           * always on screen to cover for it.
           *
           * "status" rather than "alert": polite, announced at the next pause. A channel
           * closing is worth knowing, not worth interrupting a sentence for.
           */
          role="status"
        >
          {/* The dot is not the only carrier — the word is there too, so the state survives
              greyscale, colour blindness and a screen reader. */}
          <span className="rcw-operating-status-dot" aria-hidden="true" />
          {getTranslations().operatingHours[operatingHoursClosed ? 'closed' : 'open']}
        </span>
      )}
      {showCloseButton && !fullScreenMode ? (
        <button
          className="rcw-close-button"
          onClick={() => {
            if (window.parent && parentUrl) {
              window.parent.postMessage('isCloseButton', parentUrl);
            }
            toggleChat();
          }}
        >
          <img src={minus} className="rcw-close" alt="close" />
        </button>
      ) : (
        <div className="rcw-close-button-placeholder"></div>
      )}
    </div>
  ) : (
    <></>
  );
}

export default Header;
